import { IScriptLoader, ScriptLoaderOptions } from '../../interfaces/utils/IScriptLoader';

export class ScriptLoader implements IScriptLoader {
  private static defaultOptions: Required<ScriptLoaderOptions> = {
    cacheBusting: true,
    timeout: 30000
  };

  async loadScript(scriptPath: string, options: ScriptLoaderOptions = {}): Promise<string> {
    const opts = { ...ScriptLoader.defaultOptions, ...options };
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      
      let src = scriptPath;
      if (opts.cacheBusting) {
        src += (scriptPath.includes('?') ? '&' : '?') + 'v=' + Date.now();
      }
      
      script.src = src;
      
      // Use ReturnType<typeof setTimeout> to handle both browser and Node.js environments
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      
      const cleanup = () => {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        script.remove();
      };
      
      script.onload = () => {
        cleanup();
        resolve(scriptPath);
      };
      
      script.onerror = () => {
        cleanup();
        reject(new Error(`Failed to load script: ${scriptPath}`));
      };
      
      // Set timeout after handlers are defined
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error(`Script loading timeout: ${scriptPath}`));
      }, opts.timeout);
      
      document.body.appendChild(script);
    });
  }

  async loadScripts(scriptPaths: string[], options: ScriptLoaderOptions = {}): Promise<string[]> {
    const results: string[] = [];
    for (const path of scriptPaths) {
      const result = await this.loadScript(path, options);
      results.push(result);
    }
    return results;
  }
}
